import { Button, IconButton } from "@ai-teach/ui-web";
import { useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";
import type { AnalyticsTab } from "./-types";
import { useFilterUpdates } from "./-useFilterUpdates";

export function TabNavigation() {
  const filters = useSearch({ from: "/admin/analytics/" });
  const { updateFilter } = useFilterUpdates();
  const activeTab = filters.tab || "students";
  const tabsRef = useRef<HTMLDivElement>(null);

  // Determine if we're in "My" view (no filters applied)
  const isMyView = !filters.school && !filters.teacher && !filters.student;

  // Generate dynamic tab labels based on filters
  const TABS = useMemo(
    () =>
      [
        // Student tabs (3)
        { id: "students" as AnalyticsTab, label: isMyView ? "Dashboard" : "Student Dashboard" },
        {
          id: "student-progress" as AnalyticsTab,
          label: isMyView ? "My Progress" : "Student Progress",
        },
        { id: "student-goals" as AnalyticsTab, label: isMyView ? "My Goals" : "Student Goals" },

        // Teacher tabs (3)
        { id: "teacher-dashboard" as AnalyticsTab, label: "Teacher Dashboard" },
        { id: "teacher-students" as AnalyticsTab, label: "Teacher Students" },
        { id: "teacher-interventions" as AnalyticsTab, label: "Teacher Interventions" },

        // Admin tabs (3)
        { id: "admin-system" as AnalyticsTab, label: "Admin: System" },
        { id: "admin-schools" as AnalyticsTab, label: "Admin: Schools" },
        { id: "admin-fsrs-monitor" as AnalyticsTab, label: "Admin: FSRS Monitor" },
        { id: "admin-conversation-latency" as AnalyticsTab, label: "Admin: Conversation" },
      ] as const,
    [isMyView]
  );

  // Keyboard navigation for tabs (Arrow Left/Right)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!tabsRef.current) return;

      const buttons = Array.from(tabsRef.current.querySelectorAll("button"));
      const currentIndex = buttons.findIndex((btn) => btn === document.activeElement);

      if (currentIndex === -1) return;

      if (e.key === "ArrowRight") {
        e.preventDefault();
        const nextIndex = (currentIndex + 1) % buttons.length;
        buttons[nextIndex]?.focus();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        const prevIndex = (currentIndex - 1 + buttons.length) % buttons.length;
        buttons[prevIndex]?.focus();
      }
    };

    const div = tabsRef.current;
    div?.addEventListener("keydown", handleKeyDown);
    return () => div?.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <div className="border-b border-gray-200 dark:border-gray-800 mb-6">
      <div
        ref={tabsRef}
        role="tablist"
        aria-label="Analytics tabs"
        className="flex flex-wrap gap-2 gradient-scrollbar overflow-x-auto"
      >
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`tabpanel-${tab.id}`}
            id={`tab-${tab.id}`}
            tabIndex={activeTab === tab.id ? 0 : -1}
            onClick={() => updateFilter("tab", tab.id)}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? "border-primary-600 text-primary-600"
                : "border-transparent text-gray-600 dark:text-gray-400 hover:text-primary-600 hover:border-gray-300 dark:hover:border-gray-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );
}
